import { useMemo } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from "recoil";

import { usePageContext } from "@ryde/redux";
import {
  activeViewIdAtomFamily,
  companyIdSelector,
  tableColumnsSizingAF,
  updateFinalSchemaLocalStore,
  type ColumnSizingState,
} from "@ryde/shared";

import type { IColumnsSizingStore } from "../../../column-resize";

export const useTableColumnsSizingStore = (): IColumnsSizingStore => {
  const { entity } = usePageContext();
  const companyId = useRecoilValue(companyIdSelector);
  const setActiveViewId = useSetRecoilState(activeViewIdAtomFamily(entity));

  const getColumnsSizing = useRecoilCallback(
    ({ snapshot }) =>
      (): ColumnSizingState =>
        snapshot.getLoadable(tableColumnsSizingAF(entity)).getValue(),
    [entity],
  );

  const setColumnsSizing = useRecoilCallback(
    ({ set }) =>
      (columnsSizing: ColumnSizingState): void => {
        set(tableColumnsSizingAF(entity), columnsSizing);
      },
    [entity],
  );

  const commitColumnsSizing = useEvent(
    (columnsSizing: ColumnSizingState): void => {
      setColumnsSizing(columnsSizing);

      if (companyId) {
        updateFinalSchemaLocalStore({
          companyId,
          page: entity,
          finalSchema: { columnsSizing },
        });
      }

      // Widths are saved in views, so a resized grid no longer matches the active view.
      setActiveViewId(undefined);
    },
  );

  return useMemo(
    () => ({ getColumnsSizing, setColumnsSizing, commitColumnsSizing }),
    [commitColumnsSizing, getColumnsSizing, setColumnsSizing],
  );
};
